import { AuditIcon, CorporateFinIcon } from "@cynthai/icons";

import {
  AUDIT_PROJECT_TYPE,
  CORPORATE_FINANCE_PROJECT_TYPE,
  FAS_PROJECT_TYPE,
} from "@/core/lib/constants";
import type { SidebarItem } from "@/core/lib/sidebar-types";
import type { AppInfo, ProjectTypeMembership } from "@/openapi";
import { m } from "@/paraglide/messages";

type ProjectTypeSidebarContext = {
  membership: ProjectTypeMembership;
  appCapabilities: AppInfo;
};

type ProjectTypeSidebarFactory = (
  context: ProjectTypeSidebarContext,
) => SidebarItem | null;

const PROJECT_TYPE_SIDEBAR_REGISTRY: Record<string, ProjectTypeSidebarFactory> =
  {
    [AUDIT_PROJECT_TYPE]: ({ membership, appCapabilities }) => {
      if (!appCapabilities.fabric) return null;

      if (membership.is_admin) {
        return {
          label: m.auditor(),
          icon: AuditIcon,
          to: "/auditor",
          subItems: [
            { label: m.auditor_projects(), to: "/auditor/clients" },
            {
              label: m.auditor_workflow_editor(),
              to: {
                to: "/workflow-editor",
                search: { projectType: AUDIT_PROJECT_TYPE },
              },
            },
            {
              label: m.auditor_template_editor(),
              to: {
                to: "/template-editor",
                search: { projectType: AUDIT_PROJECT_TYPE },
              },
            },
            {
              label: m.auditor_validation_tests(),
              to: "/auditor/validation",
            },
          ],
        };
      }

      if (membership.is_member) {
        return {
          label: m.auditor(),
          icon: AuditIcon,
          to: "/auditor/clients",
        };
      }

      return null;
    },
    [CORPORATE_FINANCE_PROJECT_TYPE]: ({ membership, appCapabilities }) => {
      if (!appCapabilities.fabric) return null;

      if (membership.is_admin) {
        return {
          label: m.corporate_finance(),
          icon: CorporateFinIcon,
          to: "/corporate-finance/projects",
          subItems: [
            {
              label: m.corporate_finance_projects(),
              to: "/corporate-finance/projects",
            },
            {
              label: m.corporate_finance_workflow_editor(),
              to: {
                to: "/workflow-editor",
                search: { projectType: CORPORATE_FINANCE_PROJECT_TYPE },
              },
            },
            {
              label: m.corporate_finance_template_editor(),
              to: {
                to: "/template-editor",
                search: { projectType: CORPORATE_FINANCE_PROJECT_TYPE },
              },
            },
            {
              label: m.corporate_finance_validation_tests(),
              to: "/corporate-finance/validation",
            },
          ],
        };
      }

      if (membership.is_member) {
        return {
          label: m.corporate_finance(),
          icon: CorporateFinIcon,
          to: "/corporate-finance/projects",
        };
      }

      return null;
    },
    // no fabric gate: a FAS project reads nothing from the warehouse yet. the
    // CF icon stands in until FAS has its own
    [FAS_PROJECT_TYPE]: ({ membership }) => {
      if (membership.is_admin) {
        return {
          label: m.fas(),
          icon: CorporateFinIcon,
          to: "/fas/projects",
          subItems: [
            { label: m.fas_projects(), to: "/fas/projects" },
            {
              label: m.fas_workflow_editor(),
              to: {
                to: "/workflow-editor",
                search: { projectType: FAS_PROJECT_TYPE },
              },
            },
            {
              label: m.fas_template_editor(),
              to: {
                to: "/template-editor",
                search: { projectType: FAS_PROJECT_TYPE },
              },
            },
          ],
        };
      }

      if (membership.is_member) {
        return {
          label: m.fas(),
          icon: CorporateFinIcon,
          to: "/fas/projects",
        };
      }

      return null;
    },
  };

export const buildProjectTypeSidebarItems = (
  memberships: ReadonlyArray<ProjectTypeMembership>,
  appCapabilities: AppInfo,
): SidebarItem[] => {
  const items: SidebarItem[] = [];
  for (const membership of memberships) {
    const factory = PROJECT_TYPE_SIDEBAR_REGISTRY[membership.project_type];
    if (!factory) continue;
    const item = factory({ membership, appCapabilities });
    if (item) items.push(item);
  }
  return items;
};
